import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { recordStatisticsEvent } from '../../../services/statistics-service';
import useTheme from './theme';
import dt from '../../../shared/theme';
import Wire from './header-components/wire';
import ListenDropdown from './header-components/listen-dropdown';

const Banner = styled.header`
  display: flex;
  justify-content: center;
  padding: 84px 136px 140px 121px;
  background-size: cover;
  background-repeat: round;

  ${props => props.theme.isROIXLarge && `
    padding: 72px 47px 140px 32px;
  `};

  ${props => props.theme.isROILarge && `
    padding: 72px 47px 60px 32px;
  `};

  ${props => props.theme.isROIMedium && `
    background-repeat: unset;
    background-position: center;
    padding: 72px 24px 80px 32px;
  `};

  ${props => props.theme.isROISmall && `
    padding: 72px 20px 80px;
  `};

  ${props => props.theme.isROIMobile && `
    padding: 56px 20px 64px;
  `};

  > div {
    display: flex;
    max-width: 1184px;

    ${props => props.theme.isROILarge && `
      max-width: 944px;
    `};

    ${props => props.theme.isROIMedium && `
      flex-direction: column;
      max-width: unset;
    `};

    > div.outer-wire-container {
      ${props => props.theme.isROILarge && props.hasAppleRating && `
        margin-top: 16px;
      `};

      ${props => props.theme.isROIMedium && `
        max-width: 486px;
        margin-top: 0;
      `};

      ${props => props.theme.isROIMobile && `
        width: 97%;
        max-width: unset;
      `};

      > div.image-placeholder {
        width: 100%;
        height: 267px;

        ${props => props.theme.isROIMedium && `
          display: none;
        `}

        + div.inner-wire-container > section > div.text-wrapper {
          border-radius: 4px 4px 60px 4px;
        }
      }

      > div.inner-wire-container {
        position: relative;
      }
    }
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 48px;

  ${props => props.theme.isROIXLarge && `
    margin-right: 146px;
  `};

  ${props => props.theme.isROIMedium && `
    width: 100%;
    margin: 0;
  `};

  > p.description {
    color: ${props => dt(props).theme.secondaryColor};
    font-family: ${props => dt(props).theme.fontFamilyBody};
    font-weight: ${props => dt(props).theme.fontWeightRegular};
    font-size: 19px;
    line-height: 120%;
    margin: 0 0 56px;

    ${props => props.theme.isROILarge && !props.theme.isROIMedium && `
      max-width: 526px;
    `};
  }

  > div {
    display: flex;
    width: max-content;

    ${props => props.theme.isROIMedium && `
      margin-bottom: 64px;
    `};

    ${props => props.theme.isROIMobile && `
      flex-direction: column;
      align-items: center;
      width: 100%;
    `};

    button, > div > button {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 48px;
      width: fit-content;
      text-transform: uppercase;
      font-size: 17px;
      font-weight: ${props => dt(props).theme.fontWeightHeavy};
      line-height: 120%;
      border: none;
      border-radius: 3px;

      ${props => props.theme.isROIMobile && `
        width: 100%;
      `};

      &.latest-episode-link {
        color: #FFF;
        background-color: ${props => dt(props).theme.primaryColor};
        line-height: 100%;
        margin: 0 8px 0 0;
        padding: 0 24px 0 18px;

        ${props => props.theme.isROIMobile && `
          margin: 0 0 16px 0;
        `};
  
        &:hover {
          background-color: #AF8E37;
        }
  
        > span.icon-lw-listen {
          width: 38px;
          
          > span.path2::before {
            font-size: 36px;
            text-decoration: none;
            color: #FFF;
            margin-left: -8px;
          }
        }
      }

      &:hover {
        text-decoration: none;
        cursor: pointer;
      }
    }

    > div {
      ${props => props.theme.isROIMobile && `
        width: 100%;
      `};
    }
  }

  > p.apple-rating {
    color: #D7DFE7;
    font-size: 16px;
    line-height: 120%;
    text-transform: uppercase;
    margin: 0 0 40px;

    ${props => props.theme.isROIXLarge && `
      margin: 16px 0 40px;
      font-size: 19px;
    `};

    > span.stars {
      padding-right: 12px;
      position: relative;
      font-size: 16px;
      top: 1px;

      > span.icon-lw-star {
        padding-right: 4px;
      }
    }

    > span.rating {
      font-weight: ${props => dt(props).theme.fontWeightHeavy};
      padding-right: 5px;
    }
  }
`;

const Name = styled.h2`
  color: #FFF;
  font-weight: ${props => dt(props).theme.fontWeightBlack};
  text-transform: uppercase;
  margin: 0 0 32px;
  font-size: 52px;
  line-height: 100%;

  ${props => props.theme.isROIXLarge && `
    font-size: 60px;
  `};

  ${props => props.theme.isROIMobile && `
    margin: 0 0 24px;
  `};

  > span.investing {
    color: ${props => dt(props).theme.primaryColor};
  }
`;

const OffsetBg = styled.div`
  background-color: ${props => dt(props).theme.secondaryColor};
  height: 100%;
  width: 100%;
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 0;
  border-radius: 4px;
  filter: drop-shadow(0 27px 37px rgba(50, 50, 71, 0.21)) drop-shadow(0 27px 18px rgba(50, 50, 71, 0.39));

  ${props => props.theme.isROIMobile && `
    top: 8px;
    left: 8px;
  `};
`;
// padding: 0 24px 0 32px;
const BreadcrumbContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;

  ${props => dt(props).theme.isMobileOrTablet && `
    width: 100%;
    padding: 0 20px;
    overflow-x: auto;
  `};

  ${props => dt(props).theme.isLarge && `
    padding: 0 32px;
  `};

  ${props => dt(props).theme.isXLarge && `
    width: 953px;
    padding: 0;
  `};

  ${props => dt(props).theme.isXXLarge && `
    width: 1200px;
    padding: 0;
  `};
`;

export default function Header(props) {
  const {
    seriesId, description, latestWire, wireBg, appleRating, breadcrumb,
  } = props;

  const hasPodcast = _.indexOf(latestWire.mediaTypes, 'Podcast') >= 0;
  const intRating = Math.floor(appleRating);

  const handleClick = () => {
    recordStatisticsEvent({
      event: 'latest_episode_button_click',
      statisticable: 'wire',
      id: latestWire.id,
      clicked_url: `/wires/${latestWire.slug}`,
      page: window.location.pathname,
      custom_data: {
        section: 'series_header',
        page: 'series',
      },
    });
  };

  const latestEpisodeClicked = e => {
    e && e.preventDefault();
    handleClick();
    window.location = `/wires/${latestWire.slug}`;
  };

  return (
    <div style={{ backgroundImage: `url("${wireBg}")` }}>
      <BreadcrumbContainer theme={useTheme()} dangerouslySetInnerHTML={{ __html: breadcrumb }} />
      <Banner hasAppleRating={!!appleRating} theme={useTheme()}>
        <div>
          <Container theme={useTheme()}>
            {!!appleRating && (
            <p className="apple-rating">
              <span className="stars">
                {[...Array(intRating)].map(i => <span key={i} className="icon-lw-star" />)}
                {appleRating - intRating > 0 && <span className="icon-lw-star-half" />}
              </span>
              <span className="rating">
                {appleRating}
                {' '}
                Rating
              </span>
              Apple Podcasts
            </p>
            )}
            <Name>
              The Rules of
              {' '}
              <span className="investing">Investing</span>
              {' '}
              Podcast
            </Name>
            {description && (
            <p className="description">
              {description}
            </p>
            )}
            <div>
              {!!hasPodcast && (
              <button type="button" className="latest-episode-link" onClick={latestEpisodeClicked}>
                <span className="icon-lw icon-lw-listen">
                  <span className="path2" />
                </span>
                <span>Listen to Latest Episode</span>
              </button>
              )}
              <ListenDropdown seriesId={seriesId} />
            </div>
          </Container>
          <div className="outer-wire-container">
            {!latestWire.primaryImageUrl
              && <div className="image-placeholder" />}
            <div className="inner-wire-container">
              <Wire key={`series-wire-${latestWire.id}`} wire={latestWire} />
              <OffsetBg theme={useTheme()} />
            </div>
          </div>
        </div>
      </Banner>
    </div>
  );
}

Header.propTypes = {
  seriesId: PropTypes.number.isRequired,
  description: PropTypes.string,
  latestWire: PropTypes.shape({
    id: PropTypes.number.isRequired,
    slug: PropTypes.string.isRequired,
    primaryImageUrl: PropTypes.string.isRequired,
    mediaTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  wireBg: PropTypes.string.isRequired,
  appleRating: PropTypes.number.isRequired,
  /* eslint-disable-next-line react/require-default-props */
  breadcrumb: PropTypes.string,
};

Header.defaultProps = {
  description: null,
};
